/// <amd-module name="Release/EnvironmentNode/StatusComputer" />

import { ReleaseEnvironment, DeploymentAttempt, DeploymentStatus, DeploymentOperationStatus, ApprovalStatus, ReleaseApproval, ManualIntervention, DeployPhaseStatus, ManualInterventionStatus } from "Release/Client/Contracts/Release";
import { ComputedDeploymentStatus, IReleaseEnvironmentStatusInfo } from "./Types";
import * as Resources from "Release/EnvironmentNode/Resources";

export enum ReleaseEnvironmentStatusIndicator {
    Canceled = "canceled",
    Canceling = "canceling",
    Deferred = "deferred",
    Failed = "failed",
    InProgress = "inprogress",
    PreApprovalPending = "preapprovalpending",
    PostApprovalPending = "postapprovalpending",
    PreEvaluatingGates = "preevaluatinggates",
    PostEvaluatingGates = "postevaluatinggates",
    NotDeployed = "notdeployed",
    PartiallySucceeded = "partiallysucceeded",
    Rejected = "rejected",
    Queued = "queued",
    Scheduled = "scheduled",
    Succeeded = "succeeded",
    Undefined = "undefined",
    Pending = "pending"
}

export interface IFriendlyStatus {
    iconName: string;
}

export type StatusToStringEvaluatorType = (computedStatus: ComputedDeploymentStatus) => string;
export type StatusToStatusIndicatorEvaluatorType = (computedStatus: ComputedDeploymentStatus) => ReleaseEnvironmentStatusIndicator;

export interface IReleaseEnvironmentStatusInfoComputer {

    statusText: string | StatusToStringEvaluatorType;

    statusIndicator: ReleaseEnvironmentStatusIndicator | StatusToStatusIndicatorEvaluatorType;
}



export function getComputedStatus(deployStatus: DeploymentStatus, operationStatus: DeploymentOperationStatus): ComputedDeploymentStatus {
    let computedStatus: ComputedDeploymentStatus | undefined = undefined;
    switch (deployStatus) {

        case DeploymentStatus.NotDeployed:
            computedStatus = _operationStatusMapForNotDeployedStatus[operationStatus];
            break;

        case DeploymentStatus.InProgress:
            computedStatus = _operationStatusMapForInProgressStatus[operationStatus];
            break;

        case DeploymentStatus.Failed:
            computedStatus = _operationStatusMapForFailedStatus[operationStatus] || ComputedDeploymentStatus.Failed;
            break;
    }

    if (!computedStatus) {
        computedStatus = _deploymentStatusMap[deployStatus];
    }

    return computedStatus;
}

export function getStatusInfo(computedStatus: ComputedDeploymentStatus): IReleaseEnvironmentStatusInfoComputer {
    return _computedStatusToInfoMap[computedStatus];
}

function _initialize(): any {
    // Deployment Status -> Computed status map
    _initializeDeploymentStatusMap();

    // Operation status -> Computed status map
    _initializeNotDeployedStatusMap();
    _initializeInProgressStatusMap();
    _initializeFailedStatusMap();

    _initializeComputedStatusToInfoMap();
}

function _initializeDeploymentStatusMap(): void {
    let map = _deploymentStatusMap;
    _deploymentStatusMap[DeploymentStatus.Undefined] = ComputedDeploymentStatus.Undefined;
    _deploymentStatusMap[DeploymentStatus.NotDeployed] = ComputedDeploymentStatus.NotDeployed;
    _deploymentStatusMap[DeploymentStatus.InProgress] = ComputedDeploymentStatus.InProgress;
    _deploymentStatusMap[DeploymentStatus.Succeeded] = ComputedDeploymentStatus.Succeeded;
    _deploymentStatusMap[DeploymentStatus.PartiallySucceeded] = ComputedDeploymentStatus.PartiallySucceeded;
    _deploymentStatusMap[DeploymentStatus.Failed] = ComputedDeploymentStatus.Failed;
}

function _initializeNotDeployedStatusMap(): void {
    let map = _operationStatusMapForNotDeployedStatus;
    map[DeploymentOperationStatus.Queued] = ComputedDeploymentStatus.Queued;
    map[DeploymentOperationStatus.Pending] = ComputedDeploymentStatus.PreApprovalPending;
    map[DeploymentOperationStatus.Canceled] = ComputedDeploymentStatus.CanceledBeforeExecution;
    map[DeploymentOperationStatus.PhaseCanceled] = ComputedDeploymentStatus.CanceledBeforeExecution;
    map[DeploymentOperationStatus.Deferred] = ComputedDeploymentStatus.Deferred;
    map[DeploymentOperationStatus.QueuedForAgent] = ComputedDeploymentStatus.QueuedForAgentBeforeDeploy;
    map[DeploymentOperationStatus.QueuedForPipeline] = ComputedDeploymentStatus.QueuedForPipelineBeforeDeploy;
    map[DeploymentOperationStatus.Rejected] = ComputedDeploymentStatus.PreApprovalRejected;
    map[DeploymentOperationStatus.Approved] = ComputedDeploymentStatus.InProgress;
    map[DeploymentOperationStatus.Cancelling] = ComputedDeploymentStatus.Cancelling;
    map[DeploymentOperationStatus.EvaluatingGates] = ComputedDeploymentStatus.EvaluatingPreDeploymentGates;
    map[DeploymentOperationStatus.GateFailed] = ComputedDeploymentStatus.PreDeploymentGatesFailed;
}

function _initializeInProgressStatusMap(): void {
    let map = _operationStatusMapForInProgressStatus;
    map[DeploymentOperationStatus.Pending] = ComputedDeploymentStatus.PostApprovalPending;
    map[DeploymentOperationStatus.ManualInterventionPending] = ComputedDeploymentStatus.ManualInterventionPending;
    map[DeploymentOperationStatus.Cancelling] = ComputedDeploymentStatus.Cancelling;
    map[DeploymentOperationStatus.QueuedForAgent] = ComputedDeploymentStatus.QueuedForAgentDuringDeploy;
    map[DeploymentOperationStatus.QueuedForPipeline] = ComputedDeploymentStatus.QueuedForPipelineDuringDeploy;
    map[DeploymentOperationStatus.EvaluatingGates] = ComputedDeploymentStatus.EvaluatingPostDeploymentGates;
    map[DeploymentOperationStatus.GateFailed] = ComputedDeploymentStatus.PostDeploymentGatesFailed;
}

function _initializeFailedStatusMap(): void {
    let map = _operationStatusMapForFailedStatus;
    map[DeploymentOperationStatus.Canceled] = ComputedDeploymentStatus.CanceledDuringExecution;
    map[DeploymentOperationStatus.PhaseFailed] = ComputedDeploymentStatus.TaskFailedOrManualInterventionRejected;
    map[DeploymentOperationStatus.Rejected] = ComputedDeploymentStatus.PostApprovalRejected;
    map[DeploymentOperationStatus.GateFailed] = ComputedDeploymentStatus.PostDeploymentGatesFailed;
}

export function geComputedStatusToInfoMap(): { [key: number]: IReleaseEnvironmentStatusInfoComputer } {
    return _computedStatusToInfoMap;
}

export function getPendingApprovals(statusInfo: IReleaseEnvironmentStatusInfo): ReleaseApproval[] {
    let pendingApprovals: ReleaseApproval[] = [];
    if (statusInfo.preDeploymentConditionsInfo && statusInfo.preDeploymentConditionsInfo.approvalInfo) {
        let preApprovals = statusInfo.preDeploymentConditionsInfo.approvalInfo.approvals;
        preApprovals.forEach((approval) => {
            if (approval.status === ApprovalStatus.Pending) {
                pendingApprovals.push(approval);
            }
        });
    }

    if (statusInfo.postDeploymentConditionsInfo && statusInfo.postDeploymentConditionsInfo.approvalInfo) {
        let postApprovals = statusInfo.postDeploymentConditionsInfo.approvalInfo.approvals;
        postApprovals.forEach((approval) => {
            if (approval.status === ApprovalStatus.Pending) {
                pendingApprovals.push(approval);
            }
        });
    }
    return pendingApprovals;
}

export function getFirstMIWithStatus(environment: ReleaseEnvironment, status: ManualInterventionStatus): ManualIntervention | null {
    const latestDeployment = getLatestDeploymentAttempt(environment.deploySteps);
    if (!latestDeployment) {
        return null;
    }

    let manualIntervention = null;
    latestDeployment.releaseDeployPhases.some(phase => {
        if (phase.status !== DeployPhaseStatus.InProgress) {
            return false;
        }

        return phase.manualInterventions.some(mi => {
            if (mi.status === status) {
                manualIntervention = mi;
                return true;
            }

            return false;
        });
    });

    return manualIntervention;
}


export function getLatestDeploymentAttempt(deploySteps: DeploymentAttempt[] | null | undefined): DeploymentAttempt | null | undefined {
    if (!deploySteps) {
        return null;
    }

    const maxAttempt = Math.max(...deploySteps.map(step => step.attempt));
    return deploySteps.find(step => step.attempt === maxAttempt);
}

function _initializeComputedStatusToInfoMap(): void {
    let map: { [key: number]: IReleaseEnvironmentStatusInfoComputer } = _computedStatusToInfoMap;

    // Canceled before execution.

    // Canceled before execution.
    map[ComputedDeploymentStatus.CanceledBeforeExecution] = {
        statusText: Resources.EnvironmentStatusNotDeployed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.NotDeployed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Canceled during execution.
    map[ComputedDeploymentStatus.CanceledDuringExecution] = {
        statusText: Resources.EnvironmentStatusFailed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Failed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Canceling
    map[ComputedDeploymentStatus.Cancelling] = {
        statusText: Resources.EnvironmentStatusCanceling,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Canceling,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Deferred
    map[ComputedDeploymentStatus.Deferred] = {
        statusText: Resources.EnvironmentStatusDeferred,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Deferred,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Failed
    map[ComputedDeploymentStatus.Failed] = {
        statusText: Resources.EnvironmentStatusFailed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Failed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // In Progress
    map[ComputedDeploymentStatus.InProgress] = {
        statusText: Resources.EnvironmentStatusInProgress,
        statusIndicator: ReleaseEnvironmentStatusIndicator.InProgress,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Manual intervention pending
    map[ComputedDeploymentStatus.ManualInterventionPending] = {
        statusText: Resources.PendingIntervention,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Pending,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Not started
    map[ComputedDeploymentStatus.NotDeployed] = {
        statusText: Resources.EnvironmentStatusNotDeployed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.NotDeployed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Partially succeeded.
    map[ComputedDeploymentStatus.PartiallySucceeded] = {
        statusText: Resources.EnvironmentStatusPartiallySucceeded,
        statusIndicator: ReleaseEnvironmentStatusIndicator.PartiallySucceeded,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Post approval pending
    map[ComputedDeploymentStatus.PostApprovalPending] = {
        statusText: Resources.EnvironmentStatusApprovalPending,
        statusIndicator: ReleaseEnvironmentStatusIndicator.PostApprovalPending,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Post approval rejected
    map[ComputedDeploymentStatus.PostApprovalRejected] = {
        statusText: Resources.EnvironmentStatusFailed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Failed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Pre approval pending
    map[ComputedDeploymentStatus.PreApprovalPending] = {
        statusText: Resources.EnvironmentStatusApprovalPending,
        statusIndicator: ReleaseEnvironmentStatusIndicator.PreApprovalPending,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Pre approval rejected.
    map[ComputedDeploymentStatus.PreApprovalRejected] = {
        statusText: Resources.EnvironmentStatusNotDeployed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.NotDeployed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Queued before deploy
    let queuedStatus = {
        statusText: Resources.EnvironmentStatusQueued,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Queued,
    } as IReleaseEnvironmentStatusInfoComputer;

    map[ComputedDeploymentStatus.Queued] = queuedStatus;
    map[ComputedDeploymentStatus.QueuedForAgentBeforeDeploy] = queuedStatus;
    map[ComputedDeploymentStatus.QueuedForPipelineBeforeDeploy] = queuedStatus;

    // Queued during deploy
    queuedStatus = {
        statusText: Resources.EnvironmentStatusInProgress,
        statusIndicator: ReleaseEnvironmentStatusIndicator.InProgress,
    } as IReleaseEnvironmentStatusInfoComputer;

    map[ComputedDeploymentStatus.QueuedForAgentDuringDeploy] = queuedStatus;
    map[ComputedDeploymentStatus.QueuedForPipelineDuringDeploy] = queuedStatus;

    // Scheduled
    map[ComputedDeploymentStatus.Scheduled] = {
        statusText: Resources.EnvironmentStatusScheduled,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Scheduled,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Succeeded
    map[ComputedDeploymentStatus.Succeeded] = {
        statusText: Resources.EnvironmentStatusSucceeded,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Succeeded,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Task failed or Manual intervention rejected
    map[ComputedDeploymentStatus.TaskFailedOrManualInterventionRejected] = {
        statusText: Resources.EnvironmentStatusFailed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Failed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Evaluating pre-deployment gates
    map[ComputedDeploymentStatus.EvaluatingPreDeploymentGates] = {
        statusText: Resources.EnvironmentStatusGates,
        statusIndicator: ReleaseEnvironmentStatusIndicator.PreEvaluatingGates,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Pre-deployment gates failed
    map[ComputedDeploymentStatus.PreDeploymentGatesFailed] = {
        statusText: Resources.EnvironmentStatusNotDeployed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.NotDeployed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Evaluating post-deployment gates
    map[ComputedDeploymentStatus.EvaluatingPostDeploymentGates] = {
        statusText: Resources.EnvironmentStatusGates,
        statusIndicator: ReleaseEnvironmentStatusIndicator.PostEvaluatingGates,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Post-deployment gates failed
    map[ComputedDeploymentStatus.PostDeploymentGatesFailed] = {
        statusText: Resources.EnvironmentStatusFailed,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Failed,
    } as IReleaseEnvironmentStatusInfoComputer;

    // Undefined
    map[ComputedDeploymentStatus.Undefined] = {
        statusText: Resources.EnvironmentStatusUndefined,
        statusIndicator: ReleaseEnvironmentStatusIndicator.Undefined,
    } as IReleaseEnvironmentStatusInfoComputer;
}

const _deploymentStatusMap: { [key: number]: ComputedDeploymentStatus } = {};
const _operationStatusMapForNotDeployedStatus: { [key: number]: ComputedDeploymentStatus } = {};
const _operationStatusMapForInProgressStatus: { [key: number]: ComputedDeploymentStatus } = {};
const _operationStatusMapForFailedStatus: { [key: number]: ComputedDeploymentStatus } = {};
const _computedStatusToInfoMap: { [key: number]: IReleaseEnvironmentStatusInfoComputer } = {};
const _toInit: any = _initialize();
